import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  FaBriefcase, FaHardHat, FaStar, FaMoneyBillWave,
  FaArrowUp, FaArrowDown, FaFire, FaClock,
  FaCheckCircle, FaChartLine, FaMapMarkerAlt,
} from "react-icons/fa";
import { getFullImageUrl } from "../../utils/getFullImageUrl";

// ─── Brand palette (yellow / black / white only) ───────────────────────────
const BRAND = {
  yellow: "#fff000",
  black:  "#000000",
  white:  "#ffffff",
};

// ─── Animated count-up number ──────────────────────────────────────────────
function CountUp({ value, duration = 1200, prefix = "", suffix = "", decimals = 0 }) {
  const [display, setDisplay] = useState(0);
  const start = useRef(null);
  const frame = useRef(null);

  useEffect(() => {
    const target = parseFloat(value) || 0;
    const tick = (ts) => {
      if (!start.current) start.current = ts;
      const progress = Math.min((ts - start.current) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      setDisplay(target * ease);
      if (progress < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [value, duration]);

  const formatted = decimals > 0
    ? display.toFixed(decimals)
    : Math.round(display).toLocaleString();

  return <span>{prefix}{formatted}{suffix}</span>;
}

// ─── Sparkline bar chart (last 7 jobs earnings trend) ─────────────────────
// Two-tone only: base bars in translucent black, the most recent bar
// highlighted in brand yellow.
function SparkBars({ values = [], color = BRAND.black, highlight = BRAND.yellow }) {
  const max = Math.max(...values, 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 36 }}>
      {values.map((v, i) => (
        <div key={i} style={{
          flex: 1, borderRadius: 3,
          background: i === values.length - 1 ? highlight : color + "33",
          height: `${Math.max((v / max) * 100, 8)}%`,
          transition: `height .6s cubic-bezier(.34,1.56,.64,1) ${i * 40}ms`,
        }}/>
      ))}
    </div>
  );
}

// ─── Status pill ──────────────────────────────────────────────────────────
// Status is no longer color-coded (only yellow/black/white are used
// anywhere) — each group gets its own bg/text/dot/border combination
// within the brand palette instead, so groups stay visually distinct.
function StatusPill({ status }) {
  const cfg = {
    completed:    { bg: BRAND.white,  color: BRAND.black, label: "Completed",   dot: BRAND.yellow, border: `2px solid ${BRAND.black}` },
    paid:         { bg: BRAND.white,  color: BRAND.black, label: "Paid",        dot: BRAND.yellow, border: `2px solid ${BRAND.black}` },
    inprogress:   { bg: BRAND.black,  color: BRAND.white, label: "In Progress", dot: BRAND.yellow, border: "none" },
    accepted:     { bg: BRAND.yellow, color: BRAND.black, label: "Accepted",    dot: BRAND.black,  border: "none" },
    quoteapproved:{ bg: BRAND.yellow, color: BRAND.black, label: "Approved",    dot: BRAND.black,  border: "none" },
    pending:      { bg: BRAND.white,  color: BRAND.black, label: "Pending",     dot: BRAND.black,  border: "1.5px solid rgba(0,0,0,.25)" },
    assigned:     { bg: BRAND.white,  color: BRAND.black, label: "Assigned",    dot: BRAND.black,  border: "1.5px solid rgba(0,0,0,.25)" },
    rejected:     { bg: BRAND.black,  color: BRAND.white, label: "Rejected",    dot: BRAND.white,  border: "none" },
  }[(status||"").toLowerCase().replace(/[_\s]/g,"")] || { bg:BRAND.white, color:BRAND.black, label: status, dot:"rgba(0,0,0,.4)", border: "1.5px solid rgba(0,0,0,.15)" };

  return (
    <span style={{
      background: cfg.bg, color: cfg.color, border: cfg.border,
      padding: "3px 10px", borderRadius: 50,
      fontSize: ".72rem", fontWeight: 700,
      display: "inline-flex", alignItems: "center", gap: 5,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }}/>
      {cfg.label}
    </span>
  );
}

// ─── Star rating display ──────────────────────────────────────────────────
function Stars({ rating }) {
  return (
    <span style={{ letterSpacing: 1 }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ color: i <= Math.round(rating) ? BRAND.black : "rgba(0,0,0,.15)", fontSize: ".8rem" }}>★</span>
      ))}
    </span>
  );
}

// ─── Main DashboardTab ─────────────────────────────────────────────────────
function DashboardTab({ craftsman = {}, jobs = [] }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  const stats = useMemo(() => {
    const norm = s => (s||"").toLowerCase().replace(/[_\s]/g,"");
    const done       = jobs.filter(j => ["completed","paid"].includes(norm(j.status)));
    const active     = jobs.filter(j => ["inprogress","accepted","quoteapproved","quotesubmitted"].includes(norm(j.status)));
    const pending    = jobs.filter(j => ["pending","assigned"].includes(norm(j.status)));
    const ratings    = jobs.map(j => j.rating).filter(r => r != null);
    const avgRating  = ratings.length ? (ratings.reduce((a,b) => a+b, 0)/ratings.length) : (craftsman.avg_rating || 0);
    const earnings   = done.reduce((s, j) => s + (j.payment || j.amount || j.budget || 0), 0);
    const thisMonth  = done.filter(j => {
      const d = new Date(j.completed_at || j.updated_at || 0);
      const n = new Date();
      return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
    });
    const monthEarnings = thisMonth.reduce((s, j) => s + (j.payment || j.amount || j.budget || 0), 0);

    // Sparkline: last 8 weeks of earnings (dummy fallback if no dates)
    const sparkValues = done.length >= 2
      ? Array.from({length:8}, (_,i) => {
          const w = done.filter(j => {
            const d = new Date(j.completed_at || j.updated_at || 0);
            const weeksAgo = Math.floor((Date.now() - d) / (7*24*3600*1000));
            return weeksAgo === 7-i;
          });
          return w.reduce((s,j) => s + (j.payment || j.amount || j.budget || 0), 0);
        })
      : [1200,2400,1800,3200,2800,4100,3600,monthEarnings||4800];

    return { done: done.length, active: active.length, pending: pending.length, avgRating: parseFloat(avgRating).toFixed(1), earnings, monthEarnings, sparkValues, recentJobs: [...jobs].slice(0,5) };
  }, [jobs, craftsman]);

  const profileUrl = getFullImageUrl(craftsman.profile_url);
  const greeting   = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800;900&display=swap');

        .db-wrap * { box-sizing: border-box; }
        .db-wrap { font-family: 'Raleway', sans-serif; color: #000000; }

        /* Staggered entrance */
        .db-enter { opacity: 0; transform: translateY(22px); transition: opacity .55s ease, transform .55s cubic-bezier(.34,1.3,.64,1); }
        .db-enter.show { opacity: 1; transform: translateY(0); }

        /* Hero */
        .db-hero {
          background: #000000;
          border-radius: 20px; padding: 2rem 2.5rem;
          display: flex; align-items: center; gap: 1.5rem;
          position: relative; overflow: hidden; margin-bottom: 1.75rem;
        }
        .db-hero::before {
          content: ''; position: absolute; top: -40px; right: -40px;
          width: 180px; height: 180px; border-radius: 50%;
          background: rgba(255,240,0,.10);
        }
        .db-hero::after {
          content: ''; position: absolute; bottom: -60px; right: 80px;
          width: 240px; height: 240px; border-radius: 50%;
          background: rgba(255,240,0,.06);
        }
        .db-hero-avatar {
          width: 72px; height: 72px; border-radius: 18px;
          border: 3px solid rgba(255,240,0,.5);
          object-fit: cover; flex-shrink: 0;
          box-shadow: 0 8px 24px rgba(0,0,0,.4);
        }
        .db-hero-avatar-placeholder {
          width: 72px; height: 72px; border-radius: 18px;
          background: rgba(255,240,0,.15);
          display: flex; align-items: center; justify-content: center;
          font-size: 2rem; flex-shrink: 0;
          border: 3px solid rgba(255,240,0,.4);
        }
        .db-hero-text { position: relative; z-index: 1; }
        .db-hero-greeting { font-size: .875rem; font-weight: 600; color: rgba(255,255,255,.7); margin: 0; }
        .db-hero-name { font-size: 1.75rem; font-weight: 900; color: #ffffff; margin: 0; line-height: 1.15; }
        .db-hero-meta { margin-top: .375rem; display: flex; align-items: center; gap: .75rem; flex-wrap: wrap; }
        .db-hero-badge {
          background: rgba(255,240,0,.15); color: #fff000;
          padding: 3px 12px; border-radius: 50px; font-size: .78rem; font-weight: 700;
          display: inline-flex; align-items: center; gap: 5px;
          backdrop-filter: blur(4px);
        }

        /* Stat cards grid */
        .db-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.75rem; }

        .db-stat {
          background: white; border-radius: 18px;
          padding: 1.5rem 1.5rem 1.25rem;
          border: 2px solid rgba(0,0,0,.08);
          box-shadow: 0 4px 20px rgba(0,0,0,.04);
          position: relative; overflow: hidden;
          transition: transform .25s, box-shadow .25s, border-color .25s;
          cursor: default;
        }
        .db-stat:hover { transform: translateY(-4px); box-shadow: 0 12px 36px rgba(0,0,0,.12); border-color: rgba(0,0,0,.25); }
        .db-stat::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
          border-radius: 18px 18px 0 0;
        }
        .db-stat.green::before  { background: #000000; }
        .db-stat.amber::before  { background: #fff000; }
        .db-stat.blue::before   { background: linear-gradient(90deg, #000000, #fff000); }
        .db-stat.teal::before   { background: linear-gradient(90deg, #fff000, #000000); }

        .db-stat-icon {
          width: 44px; height: 44px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.1rem; margin-bottom: .875rem; flex-shrink: 0;
        }
        .db-stat-icon.green { background: #000000; color: #fff000; }
        .db-stat-icon.amber { background: #fff000; color: #000000; }
        .db-stat-icon.blue  { background: #000000; color: #fff000; }
        .db-stat-icon.teal  { background: #fff000; color: #000000; }

        .db-stat-label { font-size: .72rem; font-weight: 700; color: rgba(0,0,0,.45); text-transform: uppercase; letter-spacing: .8px; margin-bottom: .25rem; }
        .db-stat-value { font-size: 2rem; font-weight: 900; color: #000000; line-height: 1; margin-bottom: .25rem; }
        .db-stat-sub   { font-size: .78rem; color: rgba(0,0,0,.6); font-weight: 500; display: flex; align-items: center; gap: 4px; margin-top: .25rem; }
        .db-stat-up   { color: #000000; display: inline-flex; align-items: center; gap: 3px; font-weight: 700; }
        .db-stat-down { color: #000000; display: inline-flex; align-items: center; gap: 3px; font-weight: 700; }

        /* Two column bottom section */
        .db-bottom { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
        @media (max-width: 768px) { .db-bottom { grid-template-columns: 1fr; } }

        /* Panel */
        .db-panel {
          background: white; border-radius: 18px;
          border: 2px solid rgba(0,0,0,.08);
          box-shadow: 0 4px 20px rgba(0,0,0,.04);
          overflow: hidden;
        }
        .db-panel-head {
          padding: 1.125rem 1.5rem;
          border-bottom: 2px solid rgba(0,0,0,.05);
          display: flex; align-items: center; justify-content: space-between;
        }
        .db-panel-title { font-size: .78rem; font-weight: 800; color: rgba(0,0,0,.45); text-transform: uppercase; letter-spacing: .8px; display: flex; align-items: center; gap: 6px; }
        .db-panel-body  { padding: 1.25rem 1.5rem; }

        /* Earnings chart panel */
        .db-earnings-chart { padding: 1.25rem 1.5rem 1rem; }
        .db-earnings-hero {
          display: flex; align-items: flex-end; justify-content: space-between;
          margin-bottom: 1rem;
        }
        .db-earnings-big { font-size: 2.25rem; font-weight: 900; color: #000000; line-height: 1; }
        .db-earnings-label { font-size: .75rem; color: rgba(0,0,0,.45); font-weight: 600; margin-top: 3px; }
        .db-earnings-tag {
          background: #fff000;
          color: #000000; border: 1.5px solid #000000;
          padding: 4px 12px; border-radius: 50px;
          font-size: .78rem; font-weight: 800;
          display: inline-flex; align-items: center; gap: 4px;
        }

        /* Recent jobs */
        .db-job-row {
          display: flex; align-items: center; gap: .875rem;
          padding: .75rem 0; border-bottom: 1px solid rgba(0,0,0,.05);
          transition: background .15s;
        }
        .db-job-row:last-child { border-bottom: none; padding-bottom: 0; }
        .db-job-icon {
          width: 38px; height: 38px; border-radius: 10px;
          background: #000000;
          color: #fff000; display: flex; align-items: center;
          justify-content: center; font-size: .875rem; flex-shrink: 0;
        }
        .db-job-name { font-weight: 700; font-size: .9rem; color: #000000; margin: 0; }
        .db-job-meta { font-size: .75rem; color: rgba(0,0,0,.45); display: flex; align-items: center; gap: 6px; margin-top: 2px; }
        .db-job-amount { font-weight: 800; font-size: .9rem; color: #000000; white-space: nowrap; }

        /* Quick stats row */
        .db-quick { display: flex; gap: .75rem; flex-wrap: wrap; margin-top: 1rem; }
        .db-quick-item {
          flex: 1; min-width: 80px; text-align: center;
          background: rgba(0,0,0,.02); border-radius: 12px; padding: .75rem .5rem;
          border: 1.5px solid rgba(0,0,0,.08);
        }
        .db-quick-val   { font-size: 1.375rem; font-weight: 900; color: #000000; }
        .db-quick-label { font-size: .68rem; color: rgba(0,0,0,.45); font-weight: 600; margin-top: 2px; text-transform: uppercase; letter-spacing: .5px; }

        /* Tip banner */
        .db-tip {
          background: rgba(255,240,0,.25);
          border: 2px solid #000000; border-radius: 14px;
          padding: 1rem 1.25rem; margin-top: 1.25rem;
          display: flex; align-items: flex-start; gap: 10px;
          font-size: .875rem; color: #000000; line-height: 1.5;
        }
        .db-tip-icon { font-size: 1.25rem; flex-shrink: 0; margin-top: -1px; }

        /* Responsive */
        @media (max-width: 600px) {
          .db-stats { grid-template-columns: 1fr 1fr; }
          .db-hero  { padding: 1.5rem; }
          .db-hero-name { font-size: 1.35rem; }
          .db-stat-value { font-size: 1.625rem; }
        }
        @media (max-width: 400px) {
          .db-stats { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="db-wrap">

        {/* ── Hero welcome banner ── */}
        <div className={`db-enter ${visible ? "show" : ""}`} style={{ transitionDelay: "0ms" }}>
          <div className="db-hero">
            {profileUrl
              ? <img src={profileUrl} className="db-hero-avatar" alt="Profile"/>
              : <div className="db-hero-avatar-placeholder">👷</div>
            }
            <div className="db-hero-text">
              <p className="db-hero-greeting">{greeting()},</p>
              <h2 className="db-hero-name">{craftsman.full_name || craftsman.name || "Craftsman"}</h2>
              <div className="db-hero-meta">
                {craftsman.profession && (
                  <span className="db-hero-badge"><FaHardHat size={11}/>{craftsman.profession}</span>
                )}
                {craftsman.location && (
                  <span className="db-hero-badge"><FaMapMarkerAlt size={11}/>{craftsman.location}</span>
                )}
                {craftsman.status === "approved" && (
                  <span className="db-hero-badge"><FaCheckCircle size={11}/> Verified</span>
                )}
                {stats.active > 0 && (
                  <span className="db-hero-badge"><FaFire size={11}/> {stats.active} active</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Stat cards ── */}
        <div className="db-stats">
          {[
            {
              delay: 80, color: "green",
              icon:  <FaCheckCircle/>,
              label: "Jobs Completed",
              value: stats.done,
              sub:   stats.pending > 0 ? `${stats.pending} pending` : "All caught up!",
            },
            {
              delay: 140, color: "amber",
              icon:  <FaBriefcase/>,
              label: "Active Jobs",
              value: stats.active,
              sub:   stats.active > 0 ? "Keep going " : "No active jobs",
            },
            {
              delay: 200, color: "blue",
              icon:  <FaStar/>,
              label: "Avg Rating",
              value: stats.avgRating,
              isRating: true,
              sub:   stats.avgRating >= 4.5 ? "Excellent!" : stats.avgRating >= 4 ? "Very Good" : "Keep improving",
            },
            {
              delay: 260, color: "teal",
              icon:  <FaMoneyBillWave/>,
              label: "Total Earnings",
              value: stats.earnings,
              prefix: "KSh ",
              sub:   `KSh ${stats.monthEarnings.toLocaleString()} this month`,
            },
          ].map((s, i) => (
            <div key={i}
              className={`db-enter db-stat ${s.color} ${visible ? "show" : ""}`}
              style={{ transitionDelay: `${s.delay}ms` }}>
              <div className={`db-stat-icon ${s.color}`}>{s.icon}</div>
              <div className="db-stat-label">{s.label}</div>
              <div className="db-stat-value">
                {visible
                  ? s.isRating
                    ? <><CountUp value={s.value} decimals={1}/> <span style={{fontSize:"1rem", color:"#000000"}}>★</span></>
                    : <CountUp value={s.value} prefix={s.prefix||""} duration={1400}/>
                  : s.prefix ? `${s.prefix}0` : "0"
                }
              </div>
              <div className="db-stat-sub">
                {s.sub}
              </div>
            </div>
          ))}
        </div>

        {/* ── Bottom panels ── */}
        <div className={`db-bottom db-enter ${visible ? "show" : ""}`} style={{ transitionDelay: "320ms" }}>

          {/* Earnings chart panel */}
          <div className="db-panel">
            <div className="db-panel-head">
              <div className="db-panel-title"><FaChartLine size={11}/> Earnings Trend</div>
              <span style={{ fontSize: ".72rem", color: "rgba(0,0,0,.45)", fontWeight: 600 }}>Last 8 weeks</span>
            </div>
            <div className="db-earnings-chart">
              <div className="db-earnings-hero">
                <div>
                  <div className="db-earnings-big">
                    {visible ? <CountUp value={stats.earnings} prefix="KSh " duration={1600}/> : "KSh 0"}
                  </div>
                  <div className="db-earnings-label">Total lifetime earnings</div>
                </div>
                <div className="db-earnings-tag">
                  <FaArrowUp size={10}/>
                  This month: KSh {stats.monthEarnings.toLocaleString()}
                </div>
              </div>
              <SparkBars values={stats.sparkValues}/>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                <span style={{ fontSize: ".65rem", color: "rgba(0,0,0,.3)" }}>8 wks ago</span>
                <span style={{ fontSize: ".65rem", color: "rgba(0,0,0,.3)" }}>This week</span>
              </div>
            </div>

            {/* Quick counts */}
            <div style={{ padding: "0 1.5rem 1.25rem" }}>
              <div className="db-quick">
                <div className="db-quick-item">
                  <div className="db-quick-val">{stats.done}</div>
                  <div className="db-quick-label">Done</div>
                </div>
                <div className="db-quick-item">
                  <div className="db-quick-val">{stats.active}</div>
                  <div className="db-quick-label">Active</div>
                </div>
                <div className="db-quick-item">
                  <div className="db-quick-val">{stats.pending}</div>
                  <div className="db-quick-label">Pending</div>
                </div>
                <div className="db-quick-item">
                  <div className="db-quick-val">{parseFloat(stats.avgRating).toFixed(1)}</div>
                  <div className="db-quick-label">Rating</div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent jobs panel */}
          <div className="db-panel">
            <div className="db-panel-head">
              <div className="db-panel-title"><FaClock size={11}/> Recent Jobs</div>
              {stats.recentJobs.length > 0 && (
                <span style={{ fontSize: ".72rem", color: "rgba(0,0,0,.45)", fontWeight: 600 }}>
                  {stats.recentJobs.length} shown
                </span>
              )}
            </div>
            <div className="db-panel-body" style={{ padding: "0.5rem 1.5rem 1.25rem" }}>
              {stats.recentJobs.length === 0 ? (
                <div style={{ textAlign: "center", padding: "3rem 1rem", color: "rgba(0,0,0,.4)" }}>
                  <FaBriefcase size={36} style={{ opacity: .15, display: "block", margin: "0 auto 1rem" }}/>
                  <p style={{ fontWeight: 700, color: "#000000", margin: 0 }}>No jobs yet</p>
                  <p style={{ fontSize: ".875rem", margin: ".25rem 0 0" }}>Jobs assigned to you will appear here</p>
                </div>
              ) : (
                stats.recentJobs.map((job, i) => (
                  <div key={job.id || i} className="db-job-row">
                    <div className="db-job-icon"><FaHardHat/></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="db-job-name">{job.service || job.title || "Job"}</p>
                      <div className="db-job-meta">
                        {job.location && <><FaMapMarkerAlt size={10}/>{job.location}</>}
                        {job.rating && <><Stars rating={job.rating}/></>}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                      <StatusPill status={job.status}/>
                      {(job.payment || job.amount || job.budget) ? (
                        <span className="db-job-amount">
                          KSh {(job.payment || job.amount || job.budget).toLocaleString()}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))
              )}

              {/* Tip */}
              {stats.done === 0 && (
                <div className="db-tip">
                  <span className="db-tip-icon"></span>
                  <span>
                    <strong>Complete your profile</strong> to start receiving job requests.
                    Add your services, skills, and work photos to get noticed by clients.
                  </span>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

export default DashboardTab;
